import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '@/react-app/config/api';

interface VersionData {
  version: string;
  environment: string;
  builtAt: string | null;
}

export function VersionBadge() {
  const { data, isLoading, error } = useQuery<{ success: boolean; data: VersionData }>({
    queryKey: ['version'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/version`);
      if (!response.ok) throw new Error('Failed to fetch version');
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 1,
  });

  if (isLoading || error || !data?.data) return null;

  const { version, environment, builtAt } = data.data;

  // Cores por ambiente
  const bgColor =
    environment === 'production'
      ? 'bg-green-50 border-green-200'
      : environment === 'staging'
      ? 'bg-yellow-50 border-yellow-200'
      : 'bg-blue-50 border-blue-200';

  const textColor =
    environment === 'production'
      ? 'text-green-700'
      : environment === 'staging'
      ? 'text-yellow-700'
      : 'text-blue-700';

  const formatBuildTime = (iso: string | null) => {
    if (!iso) return null;
    try {
      const date = new Date(iso);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  };

  const buildTime = formatBuildTime(builtAt);

  return (
    <div
      className={`flex items-center justify-end gap-4 border-t px-4 py-2 text-[11px] font-medium ${bgColor} ${textColor}`}
    >
      <span className="opacity-75">env:</span>
      <span className="font-semibold">{environment}</span>
      <span className="opacity-75">•</span>
      <span className="opacity-75">ver:</span>
      <span className="font-mono">{version}</span>
      {buildTime && (
        <>
          <span className="opacity-75">•</span>
          <span className="opacity-75">build:</span>
          <span>{buildTime}</span>
        </>
      )}
    </div>
  );
}
